import plusTealIcon from "@assets/images/plus-teal.svg";
import { Button, Paper } from "@material-ui/core";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import { menuWidth, shrunkMenuWidth } from "@saleor/components/Siderbar";
import { ScgOrderStatus } from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import { makeStyles } from "@saleor/macaw-ui";
import {
  formatThousandDecimal,
  shortenLongDecimal
} from "@saleor/utils/number";
import React from "react";
import InlineSVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

interface propsType {
  expandMenu?: boolean;
  bodyPaddingRight?: number;
}

const useStyles = makeStyles<propsType>(
  theme => ({
    menu: {
      padding: 0,
      position: "fixed",
      bottom: 0,
      right: 0,
      transition: "width 0.4s ease",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        padding: "0 2.4rem",
        paddingRight: props => `calc(2.4rem + ${props.bodyPaddingRight}px)`,
        width: props =>
          props.expandMenu
            ? `calc(100% - ${menuWidth}px)`
            : `calc(100% - ${shrunkMenuWidth}px)`
      }
    },
    wrapper: {
      display: "block",
      width: "100%",
      alignItems: "center",
      boxShadow: "0px -8px 24px rgb(0 0 0 / 8%)",
      borderRadius: "20px 20px 0px 0px"
    },
    section: {
      display: "flex",
      padding: "1.6rem 2.4rem",
      justifyContent: "space-between",
      gap: "0 2.4rem"
    },
    divider: {
      margin: 0,
      border: 0,
      borderTop: `1px dashed ${theme.palette.grey[500]}`
    },
    summaryCaption: {
      color: theme.palette.grey[900]
    },
    number: {
      fontSize: "1.8rem",
      lineHeight: "2.4rem"
    },
    price: {
      display: "block",
      float: "left",
      minWidth: 152,
      "&:not(:last-child)": {
        marginRight: "2.4rem"
      }
    },
    priceCaption: {
      color: theme.palette.grey[600],
      marginBottom: "0.6rem"
    },
    netPriceNumber: {
      color: theme.palette.info.dark
    },
    btnAddProduct: {
      alignItems: "center",
      height: 48,
      padding: "0 13px",
      "& .MuiButton-label": {
        fontWeight: 700,
        gap: "0 8px"
      }
    },
    btnConfirmOrder: {
      color: "#ffffff",
      alignItems: "center",
      fontSize: "1.8rem",
      lineHeight: "2.8rem",
      height: 48,
      padding: "0 73px 0 74px",
      "& .MuiButton-label": {
        fontWeight: 700
      }
    }
  }),
  { name: "CreateOrderFooter" }
);

interface CreateOrderFooterProps {
  onOrder: (type: ScgOrderStatus) => void;
  onPopup?: () => void;
  disabled?: boolean;
  disabledAddMore?: boolean;
  hasValidationError?: boolean;
  priceData: {
    itemCount: number;
    totalPrice: number;
    vat: number;
    netPrice: number;
    usd: string;
  };
  taxPercent: number;
}

const CreateOrderFooter: React.FC<CreateOrderFooterProps> = ({
  onOrder,
  onPopup,
  disabled,
  hasValidationError,
  priceData,
  disabledAddMore,
  taxPercent
}) => {
  const [appState, _] = useAppState();
  const classes = useStyles({
    expandMenu: appState.expandMenu,
    bodyPaddingRight: appState.bodyPaddingRight
  });
  const intl = useIntl();
  const { itemCount, totalPrice, vat, netPrice } = priceData;

  return (
    <div className={classes.menu}>
      <Paper className={classes.wrapper}>
        <div className={classes.section}>
          <div>
            <MyTypography
              variant="subtitle2"
              className={classes.summaryCaption}
            >
              {intl.formatMessage({
                defaultMessage: "สรุปยอดคำสั่งซื้อ"
              })}
            </MyTypography>
            <MyTypography variant="body1" className={classes.number}>
              ({itemCount}{" "}
              {intl.formatMessage({
                defaultMessage: "รายการ"
              })}
              )
            </MyTypography>
          </div>
          <div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage(
                  {
                    defaultMessage: "ยอดรวมสุทธิ ({usd})"
                  },
                  { usd: priceData.usd }
                )}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {totalPrice ? (
                  <DecimalFormat
                    value={totalPrice}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {`ภาษีมูลค่าเพิ่ม ${shortenLongDecimal(taxPercent * 100)}%`}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {vat ? (
                  <DecimalFormat
                    value={vat}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage(
                  {
                    defaultMessage: "ยอดรวมภาษีมูลค่าเพิ่ม ({usd})"
                  },
                  { usd: priceData.usd }
                )}
              </MyTypography>
              <MyTypography variant="subtitle1" className={classes.number}>
                <span className={classes.netPriceNumber}>
                  {netPrice ? formatThousandDecimal(netPrice, 2) : "0.00"}
                </span>
              </MyTypography>
            </div>
          </div>
        </div>
        <hr className={classes.divider} />
        <div className={classes.section}>
          <Button
            id="addProductButton"
            variant="outlined"
            color="secondary"
            onClick={onPopup}
            className={classes.btnAddProduct}
            disabled={disabledAddMore}
            startIcon={<InlineSVG src={plusTealIcon} />}
          >
            <span>
              <FormattedMessage defaultMessage="เพิ่มรายการสินค้า" />
            </span>
          </Button>
          <Button
            id="confirmOrderButton"
            variant="contained"
            color="secondary"
            onClick={() => onOrder(ScgOrderStatus.CONFIRMED)}
            disabled={hasValidationError || disabled}
            className={classes.btnConfirmOrder}
          >
            <FormattedMessage defaultMessage="ตกลงสั่งซื้อ" />
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export default CreateOrderFooter;
